import {useCallback, useMemo} from 'react';
import {useAppDispatch} from 'store/hooks';
import type {MetadataEditOperations, MetadataSaveOperations} from '../types';
import {
  metadataEditCancelMoveManifestationToNewExpression,
  metadataEditCancelled,
  metadataEditMoveManifestationToNewExpression,
  metadataEditMoveManifestationToOtherExpression,
} from '../metadataEditSlice';
import {useMetadataSaveOperations} from './useMetadataSaveOperations';
import {useMetadataSchemasCodelists} from './useMetadataSchemasCodelists';

type MetadataOperations = MetadataSaveOperations & MetadataEditOperations;
/**
 * Provides save and edit operations for metadata. Assumes metadata is loaded and
 * edit state is initialized.
 * @returns
 */
export const useMetadataOperations = (): MetadataOperations => {
  const dispatch = useAppDispatch();
  const saveOperations = useMetadataSaveOperations();
  const codelists = useMetadataSchemasCodelists();

  const cancelMetadataEdits = useCallback(() => {
    dispatch(metadataEditCancelled({codelists}));
  }, [codelists, dispatch]);

  const moveManifestationToOtherExpression = useCallback(
    (manifestationId: string, expressionId: string) => {
      dispatch(
        metadataEditMoveManifestationToOtherExpression({
          manifestationId,
          expressionId,
          codelists,
        }),
      );
    },
    [codelists, dispatch],
  );

  const moveManifestationToNewExpression = useCallback(
    (manifestationId: string) => {
      dispatch(
        metadataEditMoveManifestationToNewExpression({
          manifestationId,
          codelists,
        }),
      );
    },
    [codelists, dispatch],
  );

  const cancelMoveManifestationToNewExpression = useCallback(
    (manifestationId: string) => {
      dispatch(
        metadataEditCancelMoveManifestationToNewExpression({
          manifestationId,
          codelists,
        }),
      );
    },
    [codelists, dispatch],
  );

  return useMemo((): MetadataOperations => {
    return {
      ...saveOperations,
      cancelMetadataEdits,
      moveManifestationToOtherExpression,
      moveManifestationToNewExpression,
      cancelMoveManifestationToNewExpression,
    };
  }, [
    cancelMetadataEdits,
    cancelMoveManifestationToNewExpression,
    moveManifestationToNewExpression,
    moveManifestationToOtherExpression,
    saveOperations,
  ]);
};
